import React, { forwardRef, useCallback, useEffect, useRef, useState, useMemo, useLayoutEffect } from "react";
import _ from "lodash";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";
import DataTable, { CELL_STATE, ColumnTemplate, TD_TYPE, DragDOMEditor } from "@acadia-ui/datatable";
import { DataProvider } from "@acadia-ui/theme";
import Modal, { MODAL_TYPE } from "@acadia-ui/modal";
import Loading from "@acadia-ui/loading";
import ActiveIcon from "../Partials/Icons/ActiveIcon";
import TableHelper from "./TableHelper";
import ErrorNavigation from "./ErrorNavigation/ErrorNavigation";
import "./Table.scss";
import Checkbox from "@acadia-ui/checkbox";
import MediaMath from "../../Helpers/MediaMathHelpers";
import Status, { STATUS_TYPE, STATUS_SIZE } from "@acadia-ui/status";
import { TOOLBAR_EVENT_TYPE, TOOLBAR_MODE } from "@acadia-ui/toolbar";
import { ToastNotification, showToast, TOAST_TYPE } from "@acadia-ui/toast";
import EmptyState from "@acadia-ui/empty-state";
import AppSDK from "@growthos/ui-app-sdk";
import { TaxonomyCellRenderer } from "../../Views/ViewMasterCampaigns/utils";
import Heading, { HEADING_SIZE, HEADING_LEVEL } from "@acadia-ui/heading";
import useStickyColumnControls from "./useStickyColumnControls";
import useTableDisabledCellStateApi from "./useTableDisabledCellStateApi";
import { fullFilterAlgorithm } from "../../Views/WorkbookV2/features/FullFiltersSidesheet";

const DO_NOT_USE_DATA_KEY = {
	DataType: "Select",
	FieldDescription: null,
	FieldType: "Select",
	FreeFormValue: null,
	IsReadOnly: false,
	IsSubTaxonomyKey: false,
	IsSummarizedHeader: true,
	ParentName: null,
	Label: "Do Not Use",
	Name: "IsDoNotUse",
	Position: 0,
	ID: 0,
	Options: [
		{
			OptionID: "True",
			OptionLabel: "True"
		},
		{
			OptionID: "False",
			OptionLabel: "False"
		}
	]
};

const getDragCellKey = (rowId, columnBindKey) => `${rowId}::${columnBindKey}`;

const restoreHistoricalDragCells = (hierarchyLevel, historicalDragCells) => {
	for (const { rowId, columnBindKey } of historicalDragCells) {
		const cell = TableHelper.getSpecificCell(hierarchyLevel, rowId, columnBindKey);
		if (cell) {
			DragDOMEditor.ACTIONS.selectHistorical(cell);
		}
	}
};

const getClientInvalidCells = (rows, dataKeys) => {
	const invalidCells = [];
	for (const row of rows) {
		for (const { Name: columnBindKey, ValidationString: pattern } of dataKeys) {
			const value = row[columnBindKey];
			if (!pattern || value === null || value === undefined || value === "") {
				continue;
			}

			const validationInput = document.createElement("input");
			validationInput.pattern = pattern;
			validationInput.value = `${value}`;
			if (!validationInput.checkValidity()) {
				invalidCells.push({ rowId: `${row.UniqueID}`, columnBindKey });
			}
		}
	}
	return invalidCells;
};

const isApiErrorCell = ({ errors, rowId, columnBindKey, clientInvalidCellKeys }) => {
	return errors.some((error) => error.rowId === rowId && error.columnBindKey === columnBindKey) && !clientInvalidCellKeys.has(getDragCellKey(rowId, columnBindKey));
};

const getUntouchedClientErrorCells = ({ clientInvalidCells, committedCellKeys, cellStateData }) => {
	const cells = new Map();
	for (const { rowId, columnBindKey } of clientInvalidCells) {
		const cellKey = getDragCellKey(rowId, columnBindKey);
		if (committedCellKeys.has(cellKey)) {
			continue;
		}

		const rowIndex = cellStateData.findIndex((row) => `${row.UniqueID}` === rowId);
		if (rowIndex !== -1) {
			cells.set(cellKey, { rowId, columnBindKey, rowIndex });
		}
	}
	return cells;
};

// A cell that a previous drag preserved as client-invalid keeps DataTable's ERROR state until
// something clears it, and the refresh that would clear it is skipped for freeform corrections.
const getReleasedClientErrorUpdates = ({ trackedClientErrorCells, retainedClientErrorCells, cellStateData }) => {
	const updates = [];
	for (const [cellKey, { rowId, columnBindKey }] of trackedClientErrorCells) {
		if (retainedClientErrorCells.has(cellKey)) {
			continue;
		}

		const rowIndex = cellStateData.findIndex((row) => `${row.UniqueID}` === rowId);
		if (rowIndex !== -1) {
			updates.push({ rowIndex, columnKey: columnBindKey, state: CELL_STATE.DEFAULT });
		}
	}
	return updates;
};

export default forwardRef(
	(
		{
			hierarchyLevel,
			vanityName = undefined,
			data,
			config,
			metaData,
			// isEditing,
			updateSelected,
			refetchData,
			navigateToChild,
			extraColumns,
			placementAPI,
			JIRAStatus,
			gcmStatus,
			syncAPI,
			actionBarProps,
			auditLevelID,
			search,
			onSearch,
			rowHeight,
			setRowHeight,
			...props
		},
		ref
	) => {
		const dataTableApi = useRef();
		const cellStateRefreshTimeoutRef = useRef();
		const preCommitDataByIDRef = useRef(new Map());
		const dragEditRevisionsRef = useRef(new Map());
		const clientErrorCellsRef = useRef(new Map());

		const [dataProvider, setDataProvider] = useState(null);
		const [errorNavigation, setErrorNavigation] = useState([]);
		const [isLoading, setIsLoading] = useState(false);
		const [columns, setColumns] = useState([]);
		const [selectedRows, setSelectedRows] = useState([]);
		const [searchValue, setSearchValue] = useState("");
		const [errors, setErrorsState] = useState([]);
		const errorsRef = useRef(errors);
		errorsRef.current = errors;
		const setErrors = useCallback((update) => {
			if (typeof update === "function") {
				setErrorsState((previousErrors) => {
					const nextErrors = update(previousErrors);
					errorsRef.current = nextErrors;
					return nextErrors;
				});
				return;
			}

			errorsRef.current = update;
			setErrorsState(update);
		}, []);
		// A cell whose value fails its pattern never passes the data table's blur check, so it stays in edit mode holding an
		// uncontrolled editor. Bumping a revision per drag-committed cell lets the editor be keyed off it and remount with the
		// dragged value, instead of continuing to display — and later save — the value the user typed.
		const getDragEditRevision = useCallback((rowData, columnName) => dragEditRevisionsRef.current.get(`${rowData?.UniqueID}::${columnName}`) || 0, []);
		const bumpDragEditRevision = useCallback((rowId, columnName) => {
			if (rowId == null) {
				return;
			}

			const revisionKey = `${rowId}::${columnName}`;
			dragEditRevisionsRef.current.set(revisionKey, (dragEditRevisionsRef.current.get(revisionKey) || 0) + 1);
		}, []);
		const [tableSaving, setTableSaving] = useState(false);
		const [isFiltered, setIsFiltered] = useState(false);
		const [isUSCurrency, setIsUSCurrency] = useState(false);
		const [columnVisibilityOverrides, setColumnVisibilityOverrides] = useState({});

		useEffect(() => {
			MediaMath.determineIfUSCurrency([AppSDK.getContext()], () => {
				setIsUSCurrency(true);
			});
		}, []);

		useEffect(() => {
			setColumnVisibilityOverrides({});
		}, [hierarchyLevel]);

		useEffect(() => {
			return () => clearTimeout(cellStateRefreshTimeoutRef.current);
		}, []);

		const { StickyColumnControlsTrigger, StickyColumnControlsModal, stickyState, refreshTable, updateStickyScrollPositions } = useStickyColumnControls({
			hierarchyLevel,
			setIsLoading,
			// disallow checkbox column and tx column
			columns: [
				...columns?.filter((column) => {
					return !["UniqueID", TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].TaxonomyName]?.includes(column?.props?.bind);
				})
			],
			dataTableApi
		});

		const { cellState, refreshDisabledCellStates } = useTableDisabledCellStateApi({
			ref,
			dataTableApi,
			config,
			hierarchyLevel,
			errors,
			dataProvider,
			columns,
			gcmStatus,
			metaData
		});
		const scheduleCellStateRefresh = useCallback(
			(nextErrors = errorsRef.current, historicalDragCells = [], shouldRefreshCellStates = true, postDragCellStateUpdates = []) => {
				clearTimeout(cellStateRefreshTimeoutRef.current);
				cellStateRefreshTimeoutRef.current = setTimeout(() => {
					if (shouldRefreshCellStates) {
						refreshDisabledCellStates(nextErrors);
					}
					if (postDragCellStateUpdates.length > 0) {
						dataTableApi.current?.cellStateManager?.updateMultipleCellStates({
							mode: TOOLBAR_MODE.EDIT,
							updates: postDragCellStateUpdates
						});
					}
					if (historicalDragCells.length > 0) {
						// React may replace an active Textbox after drag edit, so restore DataTable's
						// historical class after the refreshed cells have rendered.
						requestAnimationFrame(() => {
							restoreHistoricalDragCells(hierarchyLevel, historicalDragCells);
						});
					}
				}, 100);
			},
			[hierarchyLevel, refreshDisabledCellStates]
		);

		useLayoutEffect(() => {
			if (tableSaving) {
				return;
			}

			const freeformFields = [];
			[...config?.TaxonomyData, ...config?.MetaData].forEach((dataKey) => {
				if (dataKey.DataType === "String") {
					freeformFields.push(dataKey.Name);
				}
			});
			const newColumns = [
				<ColumnTemplate
					// this comment exists to prevent eslint from making this unreadable.
					key="checkboxColumn"
					bind="UniqueID"
					type={TD_TYPE.CHECKBOX}
					sticky={true}
					shouldEditOnClick={false}
				>
					Checkboxes
				</ColumnTemplate>,
				<ColumnTemplate
					shouldEditOnClick={false}
					className="taxonomy-renderer-column"
					key="taxonomyColumn"
					bind={TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].TaxonomyName}
					sticky={true}
					minWidth={true}
					hasRenderCache={false}
					onRender={(bindData, rowData, rowIndex) => {
						const isEditMode = dataTableApi?.current?.toolbarApi?.current?.activeMode === TOOLBAR_MODE.EDIT;
						if ((rowData.isEdited && isEditMode) || !bindData) {
							return (
								<div style={{ display: "flex" }}>
									<Status type={STATUS_TYPE.WARNING} className="editedRowWarning" />
								</div>
							);
						}
						return <TaxonomyCellRenderer taxonomy={bindData} isPrisma={rowData.MasterPlatformID === "5"} />;
					}}
				>
					Taxonomy
				</ColumnTemplate>
			];

			if (extraColumns?.length) {
				const enabledPlatformSync = {
					placementAPI,
					JIRAStatus,
					syncAPI,
					gcmStatus
				};

				const buildDNU = () => {
					const doNotUseDataKey = {
						...DO_NOT_USE_DATA_KEY,
						defaultHidden: !extraColumns.find(({ name }) => name == "Do Not Use")?.selected
					};
					return TableHelper.createEditableColumn({
						dataKey: doNotUseDataKey,
						metaData,
						updateDataProvider,
						config,
						hierarchyLevel,
						freeformFields,
						dataProvider,
						hasErrors: errors?.length > 0,
						placementAPI,
						stickyState,
						isUSCurrency
					});
				};

				// check if there is at least one row with Draft: "True". if not, remove the column
				const showDraftColumn = data?.some((row) => row.IsDraft === "True");
				const renderedExtraColumns = showDraftColumn ? [...extraColumns] : extraColumns.filter(({ id }) => id !== "IsDraft");

				TableHelper.createExtraColumns(
					// this comment exists to prevent eslint from making this unreadable.
					newColumns,
					hierarchyLevel,
					renderedExtraColumns,
					metaData,
					enabledPlatformSync,
					buildDNU,
					stickyState
				);
			}

			if (hierarchyLevel === "Package" && metaData?.PlatformID == 5) {
				newColumns.push(
					<ColumnTemplate bind="PackageType" minWidth={true} sticky={stickyState?.PackageType} key="PackageType">
						Package Type
					</ColumnTemplate>
				);
			}

			// Position the description column first
			const isEditing = dataTableApi?.current?.toolbarApi?.current?.activeMode == TOOLBAR_MODE.EDIT;
			Object.keys(config).forEach((categoryName) => {
				if (!TableHelper.NON_ARRAY_CONFIG_FIELDS.includes(categoryName)) {
					config[categoryName].forEach((dataKey) => {
						if (TableHelper.DESCRIPTION_FIELD_NAMES.includes(dataKey.Name)) {
							newColumns.push(
								TableHelper.createEditableColumn({
									dataKey,
									metaData,
									updateDataProvider,
									config,
									hierarchyLevel,
									freeformFields,
									dataProvider,
									isEditing,
									hasErrors: errors?.length > 0,
									placementAPI,
									gcmStatus,
									navigateToChild,
									stickyState,
									isUSCurrency,
									getDragEditRevision
								})
							);
						}
					});
				}
			});

			// Build a flattened, ordered list of only those dataKeys that will actually render as summarized header columns.
			// This allows us to deterministically assign isLastColumn based on the final eligible set rather than raw config indices.
			const eligibleSummarizedDataKeys = [];
			Object.keys(config).forEach((categoryName) => {
				if (TableHelper.NON_ARRAY_CONFIG_FIELDS.includes(categoryName)) return;
				config[categoryName].forEach((dataKey) => {
					if (!TableHelper.DESCRIPTION_FIELD_NAMES.includes(dataKey.Name) && dataKey.IsSummarizedHeader && dataKey.FieldType !== "Hidden") {
						eligibleSummarizedDataKeys.push(dataKey);
					}
				});
			});

			eligibleSummarizedDataKeys.forEach((dataKey, idx) => {
				newColumns.push(
					TableHelper.createEditableColumn({
						dataKey,
						metaData,
						updateDataProvider,
						config,
						hierarchyLevel,
						freeformFields,
						dataProvider,
						isEditing,
						hasErrors: errors?.length > 0,
						placementAPI,
						gcmStatus,
						stickyState,
						isLastColumn: idx === eligibleSummarizedDataKeys.length - 1,
						isUSCurrency,
						getDragEditRevision
					})
				);
			});

			// relies on order of newColumns array for destructuring below
			const [checkboxColumn, taxonomyColumn, ...sortableColumns] = newColumns || [];

			// further extract the (position unknown) IsDraft column, if it exists, and place it right after the taxnomy column
			const draftColumnIndex = sortableColumns.findIndex((col) => col.props?.bind === "IsDraft");
			const draftColumn = draftColumnIndex > -1 ? sortableColumns.splice(draftColumnIndex, 1)[0] : undefined;

			setColumns(
				[
					checkboxColumn,
					taxonomyColumn,
					draftColumn,
					// sort newColumns so that sticky columns are at the start, but preserve relative order
					...(sortableColumns?.sort?.((a, b) => {
						const aSticky = stickyState?.[a.props.bind];
						const bSticky = stickyState?.[b.props.bind];
						if (aSticky && !bSticky) return -1;
						if (!aSticky && bSticky) return 1;
						return 0;
					}) || [])
				]
					?.filter((col) => col)
					?.filter((column, index, allColumns) => {
						// In the case of duplicate columns with the same bind key, only keep the first occurrence
						const bind = column?.props?.bind;
						if (!bind) {
							return true;
						}

						return allColumns.findIndex((candidate) => candidate?.props?.bind === bind) === index;
					})
					?.map((column) => {
						const bind = column?.props?.bind;
						const columnSettings = column?.props?.columnSettings;
						if (!bind || !columnSettings?.canHide) {
							return column;
						}

						const isVisible = columnVisibilityOverrides[bind] ?? !(columnSettings.defaultHidden ?? false);
						return React.cloneElement(column, {
							hidden: !isVisible
						});
					}) || []
			);
		}, [config, dataProvider, errorNavigation, extraColumns, errors, placementAPI, gcmStatus, tableSaving, stickyState, isUSCurrency, data, columnVisibilityOverrides]);

		useEffect(() => {
			if (dataProvider) {
				dataProvider.onDragEdit = (dragEditObject) => {
					const currentErrors = errorsRef.current || [];
					const dataKeys = [];
					Object.keys(config).forEach((categoryName) => {
						if (!TableHelper.NON_ARRAY_CONFIG_FIELDS.includes(categoryName)) {
							config[categoryName].forEach((dataKey) => {
								if (dataKey.IsSummarizedHeader && dataKey.FieldType !== "Hidden") {
									dataKeys.push(dataKey);
								}
							});
						}
					});

					const sourceData = dragEditObject.data || dataProvider.data;
					const currentDataCopy = sourceData.slice();
					const clientInvalidCells = getClientInvalidCells(currentDataCopy, dataKeys);
					const clientInvalidCellKeys = new Set(clientInvalidCells.map(({ rowId, columnBindKey }) => getDragCellKey(rowId, columnBindKey)));
					const currentPageRows = dataProvider.currentData || [];
					const getCommitDataIndex = (commit) => {
						const currentPageRow = currentPageRows[commit.rowIndex];
						const currentPageRowID = currentPageRow?.UniqueID;

						if (currentPageRowID != null) {
							return currentDataCopy.findIndex((row) => row.UniqueID === currentPageRowID);
						}

						return commit.rowIndex + dataProvider.rowsPerPage * dataProvider.currentPage;
					};
					const getPreviousRow = (dataIndex) => {
						const currentRow = currentDataCopy[dataIndex];
						return preCommitDataByIDRef.current.get(currentRow?.UniqueID ?? `index:${dataIndex}`);
					};
					const commitKey = dragEditObject.commits[0]?.key;
					const matchingDataKey = dataKeys.find((column) => column.Name == commitKey) || (commitKey === DO_NOT_USE_DATA_KEY.Name ? DO_NOT_USE_DATA_KEY : undefined);
					const historicalDragCells = [];
					dragEditObject.commits.forEach((commit) => {
						const dataIndex = getCommitDataIndex(commit);

						if (dataIndex === -1 || !currentDataCopy[dataIndex]) {
							return;
						}

						if (currentDataCopy[dataIndex] === sourceData[dataIndex]) {
							currentDataCopy[dataIndex] = structuredClone(sourceData[dataIndex]);
						}

						let disabledCell = TableHelper.checkIfCellDisabled({
							dataKey: matchingDataKey,
							hierarchyLevel,
							rowData: currentDataCopy[dataIndex]
						});
						let options = [];

						if (!disabledCell && matchingDataKey?.DataType == "Select") {
							options = TableHelper.calculateDropdownOptions({
								dataKey: matchingDataKey,
								hierarchyLevel,
								rowData: currentDataCopy[dataIndex],
								config
							});
							disabledCell = options.missingParentValue;
						}

						if (!disabledCell) {
							if (matchingDataKey?.DataType == "Select") {
								if (
									options.dropdownOptions.find((option) => {
										return option.label === commit.value;
									})
								) {
									const dataKeysInConfig = [...config.TaxonomyData, ...config.MetaData];
									currentDataCopy[dataIndex][commit.key] = commit.value ? commit.value : "";
									currentDataCopy[dataIndex].isEdited = true;
									TableHelper.CBLCascadingDelete(currentDataCopy, dataIndex, [commit.key], dataKeysInConfig);
								} else if (!commit.value) {
									// this is a clear action. null out the value.
									const dataKeysInConfig = [...config.TaxonomyData, ...config.MetaData];
									currentDataCopy[dataIndex][commit.key] = "";
									currentDataCopy[dataIndex].isEdited = true;
									TableHelper.CBLCascadingDelete(currentDataCopy, dataIndex, [commit.key], dataKeysInConfig);
								} else {
									// If the new option isn't available in the available dropdown options for this cell, we want to keep the value in the cell the same that it previously was
									currentDataCopy[dataIndex][commit.key] = getPreviousRow(dataIndex)?.[commit.key] || "";
								}
							} else {
								currentDataCopy[dataIndex][commit.key] = commit.value;
								currentDataCopy[dataIndex].isEdited = true;
								bumpDragEditRevision(currentDataCopy[dataIndex].UniqueID, commit.key);
								// API-error corrections return to the default state below. Client-side
								// validation corrections keep DataTable's historical drag treatment.
								const apiErrorCell = isApiErrorCell({
									errors: currentErrors,
									rowId: currentDataCopy[dataIndex].UniqueID,
									columnBindKey: commit.key,
									clientInvalidCellKeys
								});
								if (!apiErrorCell) {
									historicalDragCells.push({ rowId: currentDataCopy[dataIndex].UniqueID, columnBindKey: commit.key });
								}
							}
						} else {
							// If the cell is disabled, we want to keep the value in the cell the same that it previously was
							currentDataCopy[dataIndex][commit.key] = getPreviousRow(dataIndex)?.[commit.key] || "";
						}
					});
					// createNewEmptyRows(currentDataCopy);
					// for each error, look at the rowId and columnBindKey and use it to determine whether to remove it, based on whether the value changed
					const modifiedErrorCells = dragEditObject?.commits.reduce((acc, commit) => {
						const dataIndex = getCommitDataIndex(commit);
						const cellWithErrorThatWasModified = currentErrors.find(
							({ rowId, columnBindKey }) => currentDataCopy[dataIndex]?.UniqueID === rowId && columnBindKey === commit.key
						);

						return cellWithErrorThatWasModified ? [...acc, cellWithErrorThatWasModified] : acc;
					}, []);
					const cellStateData = dataProvider.filteredData ?? dataProvider.data ?? [];
					const committedCellKeys = new Set(
						dragEditObject.commits.map((commit) => {
							const dataIndex = getCommitDataIndex(commit);
							return getDragCellKey(currentDataCopy[dataIndex]?.UniqueID, commit.key);
						})
					);
					const untouchedClientErrorCells = getUntouchedClientErrorCells({ clientInvalidCells, committedCellKeys, cellStateData });
					const releasedClientErrorUpdates = getReleasedClientErrorUpdates({
						trackedClientErrorCells: clientErrorCellsRef.current,
						retainedClientErrorCells: untouchedClientErrorCells,
						cellStateData
					});
					clientErrorCellsRef.current = untouchedClientErrorCells;
					const untouchedClientErrorUpdates = [...untouchedClientErrorCells.values()].map(({ rowIndex, columnBindKey }) => ({
						rowIndex,
						columnKey: columnBindKey,
						state: CELL_STATE.ERROR
					}));
					const postDragCellStateUpdates = [...releasedClientErrorUpdates, ...untouchedClientErrorUpdates];
					const modifiedApiErrorCells = modifiedErrorCells.filter(({ rowId, columnBindKey }) => !clientInvalidCellKeys.has(getDragCellKey(rowId, columnBindKey)));
					const correctedCellStateUpdates = modifiedApiErrorCells.reduce((acc, { rowId, columnBindKey }) => {
						const rowIndex = cellStateData.findIndex((row) => row.UniqueID === rowId);

						return rowIndex === -1 ? acc : [...acc, { rowIndex, columnKey: columnBindKey, state: CELL_STATE.DEFAULT }];
					}, []);

					if (correctedCellStateUpdates.length > 0) {
						dataTableApi.current?.cellStateManager?.updateMultipleCellStates({
							mode: TOOLBAR_MODE.EDIT,
							updates: correctedCellStateUpdates
						});
					}

					const remainingErrors = currentErrors.filter(
						({ rowId, columnBindKey }) => !modifiedErrorCells?.find((mec) => mec.rowId === rowId && mec.columnBindKey === columnBindKey)
					);
					if (modifiedErrorCells.length > 0) {
						setErrors(remainingErrors);
					}
					if (matchingDataKey?.DataType === "Select" || modifiedErrorCells.length > 0 || historicalDragCells.length > 0 || postDragCellStateUpdates.length > 0) {
						const shouldRefreshCellStates = matchingDataKey?.DataType === "Select" || modifiedErrorCells.length > 0;
						scheduleCellStateRefresh(remainingErrors, historicalDragCells, shouldRefreshCellStates, postDragCellStateUpdates);
					}
					return currentDataCopy;
				};
			}
		}, [bumpDragEditRevision, dataProvider, errorNavigation, scheduleCellStateRefresh, setErrors]);

		useEffect(() => {
			setErrorNavigation([]);

			// setRefreshTable(true);
			let numberOfEmptyRows = Math.floor(
				(document.body.scrollHeight -
					66.4 - // Height of shell header
					document.querySelector(".gos-secondary-nav")?.scrollHeight - // Height of navigation
					document.querySelector(".workbook-header-container")?.scrollHeight - // Height of workbook header
					document.querySelector(".tab__wrapper")?.scrollHeight - // Height of hierarchy level tabs
					document.querySelector(".toolbar__container")?.scrollHeight - // Height of table toolbar
					48 - // Height of table header
					data.length * 60) / // Combined height of all the rows with data
					60
			);
			if (numberOfEmptyRows < 1) {
				numberOfEmptyRows = 1;
			}

			const dataWithEmptyRows = [...data];
			if (hierarchyLevel !== "LineItem") {
				for (let i = 0; i < numberOfEmptyRows; i++) {
					dataWithEmptyRows.push({ UniqueID: dataWithEmptyRows.length });
				}
			}

			// Format the dates to always be in the MM/DD/YYYY format
			const dateDataKeys = [...config.TaxonomyData, ...config.MetaData, ...config[TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].DataBucketName]].filter((dataKey) => {
				return dataKey.DataType === "Date";
			});
			dataWithEmptyRows.forEach((row) => {
				dateDataKeys.forEach((dataKey) => {
					if (row[dataKey.Name]) {
						try {
							const date = new Date(row[dataKey.Name]);
							row[dataKey.Name] = `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
						} catch {}
					}
				});
			});

			let dp;
			if (!dataProvider) {
				dp = new DataProvider({
					data: dataWithEmptyRows,
					rowsPerPage: 20,
					totalRows: dataWithEmptyRows.length,
					dragEditKey: "UniqueID",
					searchQuery: searchValue
				});
				const get = dp.get.bind(dp);
				dp.get = (params) => {
					const isFocusCellLookup = params?.currentPage === 0 && params?.rowsPerPage === dp.data?.length && params.rowsPerPage > dp.rowsPerPage;

					if (!isFocusCellLookup) {
						return get(params);
					}

					// focusCell queries all rows through get(), which otherwise replaces the active page edit state.
					const pageState = {
						currentData: dp.currentData,
						editStack: dp.editStack,
						totalRows: dp.totalRows,
						filteredData: dp.filteredData
					};
					const response = get(params);

					dp.currentData = pageState.currentData;
					dp.editStack = pageState.editStack;
					dp.totalRows = pageState.totalRows;
					dp.filteredData = pageState.filteredData;

					return response;
				};
				const save = dp.save.bind(dp);
				dp.save = (editStack) => {
					const activeEditStack = editStack || dp.editStack;

					// Ignore DataTable's empty autosaves so they cannot overwrite the pre-commit drag snapshot.
					if (activeEditStack?.stack?.length > 0) {
						preCommitDataByIDRef.current = new Map((dp.data || []).map((row, index) => [row?.UniqueID ?? `index:${index}`, _.cloneDeep(row)]));
					}

					return save(editStack);
				};
			} else {
				dp = dataProvider;
			}

			dp.doSearch = (rows, searchQuery, searchIgnoreKeys) => {
				// We need to include TaxonomyData and MetaData fields because they contain the previously set values for the row. If we do not include them as part
				// of the search and we edit a cell and it no longer matches the search criteria, that row will be removed from the search results while still in edit mode
				const parentDataKeys = config?.ParentData?.map(({ Name }) => Name) || [];
				const placementDataKeys = config?.[`${hierarchyLevel}Data`]?.map(({ Name }) => Name) || [];
				const taxonomyDataKeys = config?.TaxonomyData?.map(({ Name }) => Name) || [];
				const metaDataKeys = config?.MetaData?.map(({ Name }) => Name) || [];
				const platformIDKeys = ["GoogleCampaignManagerPK", "PlatformPlacementPK", "PlatformPackagePK", "PlatformInsertionOrderPK", "PlatformLineItemPK", "PackageType"];
				const keysToInclude = [...parentDataKeys, ...placementDataKeys, ...taxonomyDataKeys, ...metaDataKeys, "TaxonomyData", "MetaData", ...platformIDKeys];
				searchIgnoreKeys = Object.keys(rows[0])?.filter((key) => {
					if (keysToInclude?.includes(key)) {
						return false;
					}
					return true;
				});

				// Normalize values to compare while ignoring whitespace and case
				const normalize = (v) => {
					if (v === null || v === undefined) return "";
					try {
						return String(v).toLowerCase().replace(/\s+/g, "");
					} catch {
						return "";
					}
				};
				const normalizedQuery = normalize(searchQuery);

				return rows.filter((row) => {
					// We want to include cloned or multiplied rows by default even if they don't match the search query
					if (!row.UniqueID || row.UniqueID?.startsWith("duplicate")) {
						return true;
					}
					if (row.isEdited) {
						return true;
					}
					for (const key in row) {
						if (searchIgnoreKeys && searchIgnoreKeys.indexOf(key) !== -1) {
							continue;
						}

						const val = row[key];
						if (val) {
							// substring match for strings, ignoring whitespace and case
							if (!["TaxonomyData", "MetaData"].includes(key) && typeof val === "string" && normalize(val).indexOf(normalizedQuery) != -1) {
								return true;
							}

							// equality match ignoring whitespace differences
							if (typeof val !== "string") {
								const numericQuery = Number(String(searchQuery ?? "").replace(/\s+/g, ""));
								if (!Number.isNaN(numericQuery) && val == numericQuery) {
									return true;
								}
							}
							if (normalize(val) === normalizedQuery) {
								return true;
							}

							if (["TaxonomyData", "MetaData"].includes(key)) {
								try {
									// Check if a value in this row previously matched the searchQuery. If so, we don't want to remove it from the search while the table
									// is still in editing mode
									const matchingValue = JSON.parse(val)?.find((dataKey) => {
										if (!dataKey.IsFreeFormValue) {
											const fieldToCheck = dataKey?.DataValue?.DataValueShortDescr || dataKey?.DataValue?.DataValueDescription;
											if (fieldToCheck) {
												return normalize(fieldToCheck).indexOf(normalizedQuery) != -1;
											}
											return false;
										}
										const ff = dataKey?.DataValueIfFreeForm;
										return ff ? normalize(ff).indexOf(normalizedQuery) != -1 : false;
									});
									if (matchingValue) {
										return true;
									}
								} catch {}
							}
						}
					}

					return false;
				});
			};

			if (props.doFilter) {
				dp.doFilter = (rows, filters) => {
					filters.length ? setIsFiltered(true) : setIsFiltered(false);
					return props.doFilter(rows, filters);
				};
			} else {
				dp.doFilter = (rows) => {
					return rows;
					// noop to supress warning
				};
			}

			// We don't want the data provider to update if the table did not successfully save and errors were returned. ACTMASTER-836
			if (!tableSaving) {
				if (!dataProvider) {
					setDataProvider(dp);
				} else {
					dataProvider.searchQuery = searchValue;
					dataProvider.update(dataWithEmptyRows);
				}
			}

			if (search && searchValue !== search) {
				setSearchValue(search);
			}
			if (searchValue) {
				setTimeout(() => {
					dataTableApi?.current?.toolbarApi?.current?.setSearchValue(searchValue);
				}, 100);
			}
			setSelectedRows([]);
			updateSelected([]);
		}, [data]);

		useEffect(() => {
			const isEditing = dataTableApi?.current?.toolbarApi?.current?.activeMode == TOOLBAR_MODE.EDIT;
			if (errors?.length > 0 && !isLoading && isEditing) {
				const { rowId, columnBindKey } = errors[0];
				if (rowId && columnBindKey) {
					dataTableApi?.current?.focusCell(rowId, columnBindKey);
				}
			}
		}, [isLoading, errors]);

		function updateDataProvider(newValue, rowIndex, columnName, checkForCBL = false, performMediaMath = false) {
			// initial null check
			if (rowIndex === -1) {
				console.warn(`Invalid row index. ${newValue} | ${rowIndex} | ${columnName}`);
				return;
			}

			// error handling logic
			if (errors.length > 0) {
				setErrors((prevErrors) => {
					const newErrors = prevErrors?.filter((error) => {
						if (
							dataProvider.data[rowIndex]?.UniqueID === error.rowId && // match the row
							columnName === error.columnBindKey && // match the column
							dataProvider.data[rowIndex][error.columnBindKey] !== newValue // check if the value is actually different
						) {
							return false;
						}
						return true;
					});
					// avoid state update if possible.
					if (JSON.stringify(newErrors) === JSON.stringify(prevErrors)) {
						return prevErrors;
					}
					// imlemented 832 fix... delete after testing
					// setTimeout(() => {
					// delay this slightly because the newErrors will remount the component
					// to be replaced with Acadia fix: https://techjira.publicis.com/browse/ACADIA-832
					// TableHelper.fixErrorPaginator(hierarchyLevel);
					// }, 1000);
					return newErrors;
				});
			}

			// CBL Cascading delete logic
			const dataCopy = _.cloneDeep(dataProvider.data);

			// update only after the error check so we can reference old value from DP in error check
			dataProvider.update(dataCopy);
			dataProvider.save();

			dataCopy[rowIndex][columnName] = newValue ? newValue : "";
			dataCopy[rowIndex].isEdited = true;
			if (checkForCBL) {
				const dataKeysInConfig = [...config.TaxonomyData, ...config.MetaData, ...config[TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].DataBucketName]];
				TableHelper.CBLCascadingDelete(dataCopy, rowIndex, [columnName], dataKeysInConfig);
			}
			if (performMediaMath) {
				if (!metaData.Budget) {
					dataCopy[rowIndex].Cost = "0.00";
				} else {
					const totalCost = MediaMath.performTableMath(dataCopy[rowIndex]);
					dataCopy[rowIndex].Cost = totalCost === 0 ? "0.00" : totalCost;
				}
			}
			if (checkForCBL) {
				setTimeout(() => {
					refreshDisabledCellStates();
				}, 100);
			}
		}

		async function saveTable() {
			const dataCopy = _.cloneDeep(dataProvider.data);
			// Starting a drag from a cell that is still being edited cancels that cell's blur, so its row never gets
			// flagged as edited. The data table still records the commit, so treat that as a second source of truth.
			const committedRowIDs = new Set((dataProvider.commitHistory || []).flatMap(({ commits }) => commits?.map(({ rowId }) => rowId) || []).filter(Boolean));
			const rowsToSave = [];
			dataCopy.forEach((rawRow, index) => {
				// remove these UI only properties so they don't improperly cause rows to fail an equality check
				const { GoogleCampaignManagerPK, isEdited, ...row } = rawRow;

				const rowID = dataCopy[index][TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].IDName];
				if (rowID) {
					// const oldRowData = TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].dataStore.getData().find((rowData) => {
					// 	return rowData[TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].IDName] === rowID;
					// });
					// if (!_.isEqual(oldRowData, row)) {
					// If any values were edited, we want to save the row, otherwise we ignore it
					if (isEdited || committedRowIDs.has(row.UniqueID)) {
						rowsToSave.push(row);
					}
				} else {
					// If there is no ID, that means it's a new row and we'll always want to save it
					rowsToSave.push(row);
				}
			});

			const formattedPostObj = TableHelper.formatPostObj({
				data: rowsToSave,
				config,
				hierarchyLevel,
				metaData,
				auditLevelID
			});
			if (formattedPostObj.length > 0) {
				setIsLoading(true);
				// These two are for clearing out the error cell class and the error navigation array
				setErrors([]);

				const hierarchyConstants = TableHelper.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel];
				hierarchyConstants
					.saveTable(formattedPostObj)
					.then(async (response) => {
						await refetchData(true);
						scheduleCellStateRefresh([]);
						dataProvider.commitHistory = [];
						setTableSaving(false);
						showToast(
							"workbookToasts",
							<ToastNotification type={TOAST_TYPE.SUCCESS} message={`${vanityName || hierarchyLevel}(s) updated successfully.`} timed={8000} />
						);
					})
					.catch(async (errorResponse) => {
						const rawErrors = await errorResponse.details.json();
						const reconcileSuccessfulSave = (row) => {
							const saveResult = rawErrors?.[row.UniqueID];
							const savedRow = saveResult?.Object;

							if (saveResult?.Result !== true || savedRow?.UniqueID == null || savedRow?.[hierarchyConstants.IDName] == null) {
								return row;
							}

							const reconciledRow = {
								...row,
								UniqueID: savedRow.UniqueID,
								[hierarchyConstants.IDName]: savedRow[hierarchyConstants.IDName],
								isEdited: false
							};

							if (savedRow[hierarchyConstants.TaxonomyName] !== undefined) {
								reconciledRow[hierarchyConstants.TaxonomyName] = savedRow[hierarchyConstants.TaxonomyName];
							}

							return reconciledRow;
						};

						dataProvider.update(dataProvider.data.map(reconcileSuccessfulSave));
						if (dataProvider.currentData) {
							dataProvider.set(dataProvider.currentData.map(reconcileSuccessfulSave));
						}
						if (dataProvider.filteredData) {
							dataProvider.filteredData = dataProvider.filteredData.map(reconcileSuccessfulSave);
						}

						const validHeaders = Object.keys(config)
							?.filter((dataBucket) => {
								if (!["RegEx", "ChildData"].includes(dataBucket)) {
									return dataBucket;
								}
							})
							?.reduce((acc, curr) => [...acc, ...config[curr]], [])
							?.filter(({ IsSummarizedHeader }) => IsSummarizedHeader)
							?.map(({ Name }) => Name);
						const errorMessages = Object.entries(rawErrors)?.reduce((acc, [rowId, value]) => {
							const row = dataProvider?.data?.find(({ UniqueID }) => UniqueID === rowId);
							if (!value?.Result && row) {
								value?.Object?.InputErrors?.forEach((Error) => {
									const [columnBindKey, message] = Object.entries(Error)?.[0] || {};
									if (validHeaders?.includes(columnBindKey)) {
										acc.push({ rowId, message, columnBindKey });
									}
								});
							}
							return acc;
						}, []);

						dataTableApi?.current?.toolbarApi?.current?.changeActiveMode(TOOLBAR_MODE.EDIT);
						setErrors(errorMessages);
						showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={`${vanityName || hierarchyLevel}(s) could not be updated.`} />);
					})
					.finally(() => {
						setIsLoading(false);
					});
			} else {
				await refetchData();
				scheduleCellStateRefresh([]);
				dataTableApi?.current?.toolbarApi?.current?.changeActiveMode(TOOLBAR_MODE.DEFAULT);
			}
		}

		function handleRowSelection(_, node) {
			const checkboxState = node?.selectionState;
			let newSelectedRows = [...selectedRows];
			const isSelectAll = node.value === `${hierarchyLevel}__table-select-all`;
			const selectedUniqueIDs = newSelectedRows.map((row) => {
				return row.UniqueID;
			});

			if (!isSelectAll) {
				if (checkboxState === true && !selectedUniqueIDs.includes(node?.renderProps?.value)) {
					const selectedRow = dataProvider?.data?.find((row) => {
						return row.UniqueID === node?.renderProps?.value;
					});
					selectedRow.Row = dataProvider?.data?.indexOf(selectedRow);
					newSelectedRows.push(selectedRow);
				} else if (checkboxState === false) {
					newSelectedRows = newSelectedRows.filter((row) => {
						return row.UniqueID !== node.renderProps.value;
					});
				}
			} else {
				if (checkboxState === true) {
					let filteredData = dataProvider?.data;
					// ACTMASTER-5151 : Because the Placement tab uses the default data table search/filter rather than
					// updating the entire data set passed to Table.jsx, we need to handle the select all different to
					// only select the rows that have been filtered rather than all rows. This will be resolved automatically
					// when updating to the Acadia Data Table and lines 685 to 713 can be removed.
					// if (hierarchyLevel === "Placement") {
					filteredData = fullFilterAlgorithm({
						handleFilter: () => {},
						config,
						data: dataProvider?.data,
						dataTableApi,
						filterOptions: Object.values(dataProvider?.filters || {}) || [],
						hierarchyLevel
					});
					const filteredDataIds = filteredData.map((filteredPlacements) => {
						return filteredPlacements.UniqueID;
					});
					const newTree = dataTableApi.current.rowSelectionManager.tree;
					let numberOfRowsSelected = 0;
					newTree.children.loadedNodes.forEach((node) => {
						if (!filteredDataIds.includes(node?.renderProps?.value)) {
							node.selectionState = false;
						}
						if (filteredDataIds.includes(node?.renderProps?.value) && node.selectionState === true) {
							numberOfRowsSelected++;
						}
					});
					dataTableApi?.current?.rowSelectionManager?.setTree(newTree);
					dataTableApi?.current?.rowSelectionManager?.setNumRowsSelected(numberOfRowsSelected);
					// }
					filteredData?.forEach((row, index) => {
						if (!selectedUniqueIDs.includes(row.UniqueID)) {
							row.Row = index;
							newSelectedRows.push(row);
						}
					});
				} else {
					const uniqueIDsOnCurrentPage = dataProvider?.data?.map((row) => {
						return row.UniqueID;
					});
					newSelectedRows = newSelectedRows.filter((row) => {
						return !uniqueIDsOnCurrentPage.includes(row.UniqueID);
					});
				}
			}
			setSelectedRows(newSelectedRows);
			updateSelected(newSelectedRows);
		}

		const handleToolbarEvent = async (eventType, { value }) => {
			switch (eventType) {
				case TOOLBAR_EVENT_TYPE.EDIT_SAVE:
					setTableSaving(true);
					await saveTable();
					break;
				case "mode-change":
					setTableSaving(false);
					break;
				case TOOLBAR_EVENT_TYPE.EDIT_CANCEL:
					setIsLoading(true);
					setErrors([]);
					await refetchData();
					scheduleCellStateRefresh([]);
					dataProvider.commitHistory = [];
					setIsLoading(false);
					const historicallySelectedElements = document.querySelectorAll(`#${hierarchyLevel}__table .${DragDOMEditor.dragTargetHistoricalClassName}`);
					historicallySelectedElements?.forEach((el) => DragDOMEditor.ACTIONS.deselectHistorical(el));
					break;
				case TOOLBAR_EVENT_TYPE.SEARCH_CHANGE:
					setSearchValue(value);
					onSearch?.(value);
					break;
				case "settings-sidesheet-saved":
					if (value && typeof value === "object") {
						setColumnVisibilityOverrides(value.columnVisibility || {});
						setRowHeight?.(value.rowHeight || "default");
					}
					break;
				default:
				// noop
			}
		};

		const empty = (
			<EmptyState
				heading={
					<Heading size={HEADING_SIZE.MEDIUM} as={HEADING_LEVEL.H2}>
						No Data
					</Heading>
				}
			></EmptyState>
		);

		const { doFilter, ...rest } = props;

		return (
			<>
				{/* {isLoading && <Loading fullscreen />} */}
				{errorNavigation.length > 0 && <ErrorNavigation errorNavigation={errorNavigation} hierarchyLevel={hierarchyLevel} />}
				{!refreshTable && (
					<DataTable
						isLoading={isLoading}
						ref={dataTableApi}
						className="workbookTable"
						id={`${hierarchyLevel}__table`}
						bindKey="UniqueID"
						data={dataProvider}
						hasVirtualScrolling={false}
						scroll={true}
						onScroll={(e) => {
							updateStickyScrollPositions({ dataTableApi });
							if (document.activeElement.tagName === "INPUT") {
								const activeElementIsInLastColumn = document?.activeElement?.closest?.("td") == document?.activeElement?.closest?.("tr")?.lastChild;
								// don't run for last column. causes too many problems and the edge case will only occur at like 1000% browser zoom
								if (!activeElementIsInLastColumn) {
									document.activeElement.blur();
								}
							}
						}}
						// emptyState={
						// 	!dataProvider || (dataProvider?.currentData === null && data?.length && !searchValue) ? <Loading details="Loading table..."> </Loading> : <h2>No Data</h2>
						// }
						emptyState={data?.length === 0 || searchValue || isFiltered ? empty : <Loading details="Loading table..."> </Loading>}
						// resize={true}
						onCheckboxChange={(evt, chks, node) => {
							handleRowSelection(chks, node);
						}}
						paginationProps={{
							rowOptions: [
								{ label: "10", value: 10 },
								{ label: "20", value: 20 },
								{ label: "30", value: 30 },
								{ label: "40", value: 40 },
								{ label: "50", value: 50 }
							]
						}}
						height={rowHeight || "default"}
						actionBarProps={{
							search: true,
							filters: {
								quickFilters: [],
								fullFilters: []
							},
							errors,
							...actionBarProps,
							defaultModeActions: [
								// this comment exists to prevent eslint from making this unreadable.
								...(actionBarProps?.defaultModeActions || []),
								<StickyColumnControlsTrigger />
							],
							settingsSideSheet: true,
							onToolbarEvent: (...args) => {
								handleToolbarEvent(...args);
								actionBarProps?.onToolbarEvent?.(...args);
							}
						}}
						cellState={cellState}
						{...rest}
					>
						{columns}
					</DataTable>
				)}
				<StickyColumnControlsModal stickyState={stickyState} />
			</>
		);
	}
);
